/**
 * Connection Manager Service
 * Manages database connections, health checks, and connection pooling
 */

import mysql from 'mysql2/promise';
import { Pool as PgPool } from 'pg';
import { MongoClient } from 'mongodb';
import { EncryptionService } from './encryption.service';
import type {
  DbConnection,
  ConnectionConfig,
  MySQLConnectionConfig,
  PostgresConnectionConfig,
  MongoDBConnectionConfig,
  RESTAPIConnectionConfig,
  DiscoveredSchema,
  ColumnInfo,
  HealthStatus,
} from '../types';

// Connection pool cache
const connectionPools: Map<string, any> = new Map();

export class ConnectionManagerService {
  private db: any;

  constructor(db: any) {
    this.db = db;
  }

  /**
   * Create a new database connection
   */
  async createConnection(
    userId: string,
    name: string,
    config: ConnectionConfig,
    options?: {
      description?: string;
      allowedTables?: string[];
      allowedOperations?: string[];
    }
  ): Promise<DbConnection> {
    // Encrypt the connection config
    const encryptedConfig = EncryptionService.encrypt(config);

    const [connection] = await this.db
      .insert({
        userId,
        name,
        description: options?.description,
        connectorType: config.type,
        connectionConfig: encryptedConfig,
        allowedTables: options?.allowedTables,
        allowedOperations: options?.allowedOperations || ['read'],
        isActive: true,
        healthStatus: 'unknown',
      })
      .returning('*');

    // Test the connection
    await this.testConnection(connection.id);

    return connection;
  }

  /**
   * Test a database connection
   */
  async testConnection(connectionId: string): Promise<{ 
    success: boolean; 
    message: string; 
    latencyMs?: number 
  }> {
    const connection = await this.getConnectionById(connectionId);
    if (!connection) {
      return { success: false, message: 'Connection not found' };
    }

    const config = EncryptionService.decrypt<ConnectionConfig>(connection.connectionConfig);
    const startTime = Date.now();

    try {
      switch (config.type) {
        case 'mysql':
          await this.testMySQLConnection(config);
          break;
        case 'postgres':
          await this.testPostgresConnection(config);
          break;
        case 'mongodb':
          await this.testMongoDBConnection(config);
          break;
        case 'rest_api':
          await this.testRESTAPIConnection(config);
          break;
        default:
          throw new Error(`Unsupported connector type: ${(config as any).type}`);
      }

      const latencyMs = Date.now() - startTime;

      // Update health status
      await this.updateHealthStatus(connectionId, 'healthy', 'Connection successful', latencyMs);

      return { success: true, message: 'Connection successful', latencyMs };
    } catch (error: any) {
      const message = error.message || 'Connection failed';
      await this.updateHealthStatus(connectionId, 'unhealthy', message);
      return { success: false, message };
    }
  }

  /**
   * Discover schema from a connection
   */
  async discoverSchema(connectionId: string): Promise<DiscoveredSchema[]> {
    const connection = await this.getConnectionById(connectionId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    const config = EncryptionService.decrypt<ConnectionConfig>(connection.connectionConfig);

    switch (config.type) {
      case 'mysql':
        return this.discoverMySQLSchema(connectionId, config);
      case 'postgres':
        return this.discoverPostgresSchema(connectionId, config);
      case 'mongodb':
        return this.discoverMongoDBSchema(connectionId, config);
      default:
        throw new Error(`Schema discovery not supported for ${config.type}`);
    }
  }

  /**
   * Execute a query on a connection
   */
  async executeQuery(
    connectionId: string,
    query: string,
    params?: any[]
  ): Promise<{ rows: any[]; rowCount: number; executionTimeMs: number }> {
    const connection = await this.getConnectionById(connectionId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    const config = EncryptionService.decrypt<ConnectionConfig>(connection.connectionConfig);
    const startTime = Date.now();

    let rows: any[] = [];
    let rowCount = 0;

    switch (config.type) {
      case 'mysql': {
        const pool = await this.getMySQLPool(connectionId, config);
        const [result] = await pool.execute(query, params);
        rows = result as any[];
        rowCount = rows.length;
        break;
      }
      case 'postgres': {
        const pool = await this.getPostgresPool(connectionId, config);
        const result = await pool.query(query, params);
        rows = result.rows;
        rowCount = result.rowCount || rows.length;
        break;
      }
      default:
        throw new Error(`Query execution not supported for ${config.type}`);
    }

    return {
      rows,
      rowCount,
      executionTimeMs: Date.now() - startTime,
    };
  }

  /**
   * Execute an action (INSERT/UPDATE/DELETE) on a connection
   */
  async executeAction(
    connectionId: string,
    sql: string,
    params?: any[]
  ): Promise<{ affectedRows: number; insertId?: any; executionTimeMs: number }> {
    const connection = await this.getConnectionById(connectionId);
    if (!connection) {
      throw new Error('Connection not found');
    }

    // Check if write operations are allowed
    if (!connection.allowedOperations.includes('write')) {
      throw new Error('Write operations not allowed for this connection');
    }

    const config = EncryptionService.decrypt<ConnectionConfig>(connection.connectionConfig);
    const startTime = Date.now();

    let affectedRows = 0;
    let insertId: any;

    switch (config.type) {
      case 'mysql': {
        const pool = await this.getMySQLPool(connectionId, config);
        const [result] = await pool.execute(sql, params) as any;
        affectedRows = result.affectedRows || 0;
        insertId = result.insertId;
        break;
      }
      case 'postgres': {
        const pool = await this.getPostgresPool(connectionId, config);
        const result = await pool.query(sql, params);
        affectedRows = result.rowCount || 0;
        if (result.rows?.[0]?.id) {
          insertId = result.rows[0].id;
        }
        break;
      }
      default:
        throw new Error(`Action execution not supported for ${config.type}`);
    }

    return {
      affectedRows,
      insertId,
      executionTimeMs: Date.now() - startTime,
    };
  }

  // ============================================================
  // Private Helper Methods
  // ============================================================

  private async getConnectionById(connectionId: string): Promise<DbConnection | null> {
    // This would use the actual database query
    // For now, returning a placeholder
    return null;
  }

  private async updateHealthStatus(
    connectionId: string,
    status: HealthStatus,
    message: string,
    latencyMs?: number
  ): Promise<void> {
    // Update the connection's health status in the database
    console.log(`[Connection ${connectionId}] Health: ${status} - ${message}`);
  }

  // MySQL Methods
  private async testMySQLConnection(config: MySQLConnectionConfig): Promise<void> {
    const connection = await mysql.createConnection({
      host: config.host,
      port: config.port,
      database: config.database,
      user: config.username,
      password: config.password,
      ssl: config.ssl ? { rejectUnauthorized: false } : undefined,
    });
    await connection.ping();
    await connection.end();
  }

  private async getMySQLPool(connectionId: string, config: MySQLConnectionConfig): Promise<mysql.Pool> {
    if (!connectionPools.has(connectionId)) {
      const pool = mysql.createPool({
        host: config.host,
        port: config.port,
        database: config.database,
        user: config.username,
        password: config.password,
        ssl: config.ssl ? { rejectUnauthorized: false } : undefined,
        waitForConnections: true,
        connectionLimit: 10,
        queueLimit: 0,
      });
      connectionPools.set(connectionId, pool);
    }
    return connectionPools.get(connectionId);
  }

  private async discoverMySQLSchema(
    connectionId: string,
    config: MySQLConnectionConfig
  ): Promise<DiscoveredSchema[]> {
    const pool = await this.getMySQLPool(connectionId, config);
    
    // Get all tables
    const [tables] = await pool.execute(
      `SELECT TABLE_NAME, TABLE_COMMENT 
       FROM information_schema.TABLES 
       WHERE TABLE_SCHEMA = ?`,
      [config.database]
    ) as any;

    const schemas: DiscoveredSchema[] = [];

    for (const table of tables) {
      // Get columns for each table
      const [columns] = await pool.execute(
        `SELECT COLUMN_NAME, DATA_TYPE, IS_NULLABLE, COLUMN_KEY, COLUMN_DEFAULT, COLUMN_COMMENT
         FROM information_schema.COLUMNS
         WHERE TABLE_SCHEMA = ? AND TABLE_NAME = ?
         ORDER BY ORDINAL_POSITION`,
        [config.database, table.TABLE_NAME]
      ) as any;

      const columnInfos: ColumnInfo[] = columns.map((col: any) => ({
        name: col.COLUMN_NAME,
        type: col.DATA_TYPE,
        nullable: col.IS_NULLABLE === 'YES',
        isPrimary: col.COLUMN_KEY === 'PRI',
        isForeign: col.COLUMN_KEY === 'MUL',
        defaultValue: col.COLUMN_DEFAULT,
        description: col.COLUMN_COMMENT || undefined,
      }));

      schemas.push({
        id: `${connectionId}_${table.TABLE_NAME}`,
        connectionId,
        tableName: table.TABLE_NAME,
        tableDescription: table.TABLE_COMMENT || undefined,
        columns: columnInfos,
        primaryKey: columnInfos.filter(c => c.isPrimary).map(c => c.name),
        foreignKeys: [],
        discoveredAt: new Date(),
        updatedAt: new Date(),
      });
    }

    return schemas;
  }

  // PostgreSQL Methods
  private async testPostgresConnection(config: PostgresConnectionConfig): Promise<void> {
    const pool = new PgPool({
      host: config.host,
      port: config.port,
      database: config.database,
      user: config.username,
      password: config.password,
      ssl: config.ssl ? { rejectUnauthorized: false } : false,
    });
    const client = await pool.connect();
    await client.query('SELECT 1');
    client.release();
    await pool.end();
  }

  private async getPostgresPool(connectionId: string, config: PostgresConnectionConfig): Promise<PgPool> {
    if (!connectionPools.has(connectionId)) {
      const pool = new PgPool({
        host: config.host,
        port: config.port,
        database: config.database,
        user: config.username,
        password: config.password,
        ssl: config.ssl ? { rejectUnauthorized: false } : false,
        max: 10,
      });
      connectionPools.set(connectionId, pool);
    }
    return connectionPools.get(connectionId);
  }

  private async discoverPostgresSchema(
    connectionId: string,
    config: PostgresConnectionConfig
  ): Promise<DiscoveredSchema[]> {
    const pool = await this.getPostgresPool(connectionId, config);
    
    const tablesResult = await pool.query(
      `SELECT table_name, obj_description((schemaname||'.'||tablename)::regclass, 'pg_class') as comment
       FROM pg_tables
       WHERE schemaname = 'public'`
    );

    const schemas: DiscoveredSchema[] = [];

    for (const table of tablesResult.rows) {
      const columnsResult = await pool.query(
        `SELECT column_name, data_type, is_nullable, column_default
         FROM information_schema.columns
         WHERE table_schema = 'public' AND table_name = $1
         ORDER BY ordinal_position`,
        [table.table_name]
      );

      const columnInfos: ColumnInfo[] = columnsResult.rows.map((col: any) => ({
        name: col.column_name,
        type: col.data_type,
        nullable: col.is_nullable === 'YES',
        isPrimary: false, // Would need additional query
        isForeign: false,
        defaultValue: col.column_default,
      }));

      schemas.push({
        id: `${connectionId}_${table.table_name}`,
        connectionId,
        tableName: table.table_name,
        tableDescription: table.comment || undefined,
        columns: columnInfos,
        primaryKey: [],
        foreignKeys: [],
        discoveredAt: new Date(),
        updatedAt: new Date(),
      });
    }

    return schemas;
  }

  // MongoDB Methods
  private async testMongoDBConnection(config: MongoDBConnectionConfig): Promise<void> {
    const client = new MongoClient(config.connectionString);
    await client.connect();
    await client.db(config.database).command({ ping: 1 });
    await client.close();
  }

  private async discoverMongoDBSchema(
    connectionId: string,
    config: MongoDBConnectionConfig
  ): Promise<DiscoveredSchema[]> {
    const client = new MongoClient(config.connectionString);
    await client.connect();
    const db = client.db(config.database);
    
    const collections = await db.listCollections().toArray();
    const schemas: DiscoveredSchema[] = [];

    for (const collection of collections) {
      // Sample documents to infer schema
      const docs = await db.collection(collection.name).find().limit(100).toArray();
      const columnInfos = this.inferMongoDBColumns(docs);

      schemas.push({
        id: `${connectionId}_${collection.name}`,
        connectionId,
        tableName: collection.name,
        columns: columnInfos,
        primaryKey: ['_id'],
        foreignKeys: [],
        discoveredAt: new Date(),
        updatedAt: new Date(),
      });
    }

    await client.close();
    return schemas;
  }

  private inferMongoDBColumns(documents: any[]): ColumnInfo[] {
    const fieldTypes: Map<string, Set<string>> = new Map();

    for (const doc of documents) {
      this.extractFields(doc, '', fieldTypes);
    }

    const columns: ColumnInfo[] = [];
    for (const [name, types] of fieldTypes.entries()) {
      columns.push({
        name,
        type: Array.from(types).join(' | '),
        nullable: true,
        isPrimary: name === '_id',
        isForeign: false,
      });
    }

    return columns;
  }

  private extractFields(obj: any, prefix: string, fieldTypes: Map<string, Set<string>>): void {
    for (const [key, value] of Object.entries(obj)) {
      const fieldName = prefix ? `${prefix}.${key}` : key;
      const type = Array.isArray(value) ? 'array' : typeof value;
      
      if (!fieldTypes.has(fieldName)) {
        fieldTypes.set(fieldName, new Set());
      }
      fieldTypes.get(fieldName)!.add(type);

      if (type === 'object' && value !== null && !Array.isArray(value)) {
        this.extractFields(value, fieldName, fieldTypes);
      }
    }
  }

  // REST API Methods
  private async testRESTAPIConnection(config: RESTAPIConnectionConfig): Promise<void> {
    const headers: Record<string, string> = {
      ...config.headers,
    };

    if (config.authType === 'api_key' && config.authConfig?.apiKey) {
      headers[config.authConfig.apiKeyHeader || 'X-API-Key'] = config.authConfig.apiKey;
    } else if (config.authType === 'bearer' && config.authConfig?.bearerToken) {
      headers['Authorization'] = `Bearer ${config.authConfig.bearerToken}`;
    } else if (config.authType === 'basic' && config.authConfig?.username) {
      const credentials = Buffer.from(
        `${config.authConfig.username}:${config.authConfig.password}`
      ).toString('base64');
      headers['Authorization'] = `Basic ${credentials}`;
    }

    const response = await fetch(config.baseUrl, {
      method: 'HEAD',
      headers,
      signal: AbortSignal.timeout(config.timeout || 30000),
    });

    if (!response.ok && response.status !== 405) {
      throw new Error(`API returned status ${response.status}`);
    }
  }

  /**
   * Close all connection pools
   */
  static async closeAllConnections(): Promise<void> {
    for (const [id, pool] of connectionPools.entries()) {
      try {
        if (pool.end) {
          await pool.end();
        } else if (pool.close) {
          await pool.close();
        }
        console.log(`[Connection ${id}] Pool closed`);
      } catch (error) {
        console.error(`[Connection ${id}] Error closing pool:`, error);
      }
    }
    connectionPools.clear();
  }
}
